import { IconCurrencyEuro } from "@tabler/icons-react";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { euroToCents, marginPercentOfCost, parseInputNumberValue } from "#shared/helpers/prices";
import ConditionalTooltip from "#shared/view/components/ConditionalTooltip";
import { Flex } from "#shared/view/components/Flex";
import { NumberInput } from "#shared/view/designSystem/inputs/NumberInput";
import { computeQuoteRequestLinePrice, formatPrice } from "shared";

type MarginsPricesProps = {
  qrlPrice: ReturnType<typeof computeQuoteRequestLinePrice> | null;
  onMaterialMarginUpdate: (value: number) => void;
  onProductMarginUpdate: (value: number) => void;
  onAddedValueMarginUpdate: (value: number) => void;
  disabled: boolean;
};

type Extractors = {
  getCost: (q: MarginsPricesProps["qrlPrice"]) => number;
  onUpdate: (percent: number) => void;
};

export const MarginsPrices = ({
  qrlPrice,
  onMaterialMarginUpdate,
  onProductMarginUpdate,
  onAddedValueMarginUpdate,
  disabled,
}: MarginsPricesProps) => {
  const { t } = useTranslation();

  const materialPrice = qrlPrice?.prices.partMaterialPrice ?? 0;
  const addedValuePrice = qrlPrice?.prices.partAddedValueProcessesPrice ?? 0;
  const productsPrice = qrlPrice?.prices.partProductsProcessesPrice ?? 0;

  const createPriceHandler = useCallback(
    ({ getCost, onUpdate }: Extractors) =>
      (e: React.FocusEvent<HTMLInputElement>) => {
        const newPriceEuro = parseInputNumberValue(e.target.value);

        if (Number.isNaN(newPriceEuro) || newPriceEuro < 0) {
          return;
        }

        const costCents = getCost(qrlPrice);
        if (costCents < 0) {
          return;
        }

        const newPriceCents = euroToCents(newPriceEuro);
        const marginPercent = marginPercentOfCost(newPriceCents, costCents, {
          rounded: false,
        });

        onUpdate(marginPercent);
      },
    [qrlPrice],
  );

  const plainHandlers = useMemo(() => {
    return {
      material: createPriceHandler({
        getCost: (q) => q?.costs.partMaterialCost ?? 0,
        onUpdate: onMaterialMarginUpdate,
      }),
      addedValue: createPriceHandler({
        getCost: (q) => q?.costs.partAddedValueProcessesCost ?? 0,
        onUpdate: onAddedValueMarginUpdate,
      }),
      products: createPriceHandler({
        getCost: (q) => q?.costs.partProductsProcessesCost ?? 0,
        onUpdate: onProductMarginUpdate,
      }),
    };
  }, [createPriceHandler, onMaterialMarginUpdate, onAddedValueMarginUpdate, onProductMarginUpdate]);

  return (
    <Flex gap="xs">
      <ConditionalTooltip
        show={qrlPrice?.costs.materialCost === 0 && !disabled}
        label={t("qrl.margin.noMaterialCostExplanation")}
      >
        <NumberInput
          label={t("qrl.materialGradePrice")}
          leftSection={<IconCurrencyEuro size={20} />}
          value={formatPrice(materialPrice)}
          min={0}
          max={100}
          decimalScale={2}
          allowNegative={false}
          onBlur={plainHandlers.material}
          disabled={qrlPrice?.costs.materialCost === 0 || disabled}
        />
      </ConditionalTooltip>

      <ConditionalTooltip
        show={qrlPrice?.costs.addedValueProcessesCost === 0 && !disabled}
        label={t("qrl.margin.noAddedValueCostExplanation")}
      >
        <NumberInput
          label={t("qrl.margin.addedValuePrice")}
          leftSection={<IconCurrencyEuro size={20} />}
          value={formatPrice(addedValuePrice)}
          min={0}
          max={100}
          decimalScale={2}
          allowNegative={false}
          onBlur={plainHandlers.addedValue}
          disabled={qrlPrice?.costs.addedValueProcessesCost === 0 || disabled}
        />
      </ConditionalTooltip>

      <ConditionalTooltip
        show={qrlPrice?.costs.productsProcessesCost === 0 && !disabled}
        label={t("qrl.margin.noProductCostExplanation")}
      >
        <NumberInput
          label={t("qrl.margin.productMarginsPrice")}
          leftSection={<IconCurrencyEuro size={20} />}
          value={formatPrice(productsPrice)}
          min={0}
          max={100}
          decimalScale={2}
          allowNegative={false}
          onBlur={plainHandlers.products}
          disabled={qrlPrice?.costs.productsProcessesCost === 0 || disabled}
        />
      </ConditionalTooltip>
    </Flex>
  );
};
